import React, { useState, useEffect, useCallback } from "react";
import "./Productions.scss";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";

const Productions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kanbanColumns, setKanbanColumns] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [employees, setEmployees] = useState([]);

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [selectedTargetStage, setSelectedTargetStage] = useState(null);
  const [completeMessages, setCompleteMessages] = useState([""]);
  const [revertMessage, setRevertMessage] = useState("");
  const [processingStageChange, setProcessingStageChange] = useState(false);

  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverColumn, setDraggedOverColumn] = useState(null);

  const isProductionDisabled = (production) => {
    const disabledStatuses = ["COMPLETED", "RETURNED", "ASSIGNED"];
    return (
      (production.status &&
        disabledStatuses.includes(production.status.toUpperCase())) ||
      production.returned
    );
  };

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get("/api/emp/employees/list");

      if (response.status === 200 && response.data?.data) {
        setEmployees(response.data.data);
        return response.data.data;
      } else {
        throw new Error("Invalid response structure for employees");
      }
    } catch (error) {
      toast.error("Failed to load employees data");
      return [];
    }
  };

  const getAssignedEmployees = (stage) => {
    if (
      !stage.employeeIds ||
      !Array.isArray(stage.employeeIds) ||
      stage.employeeIds.length === 0
    ) {
      return [];
    }

    return employees.filter((emp) => stage.employeeIds.includes(emp.empId));
  };

  const fetchProcessData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const authorities = JSON.parse(
        sessionStorage.getItem("Authorities") || "[]"
      );

      let endpoint = "";
      if (authorities.includes("ROLE_CRM-ADMIN")) {
        endpoint = "/api/crm-admin/process-history/process-Histories";
      } else if (authorities.includes("ROLE_PRODUCTION")) {
        endpoint = `${process.env.REACT_APP_API_PROD}/process-history/process-Histories`;
      } else {
        throw new Error("User does not have the required role.");
      }

      const response = await axiosInstance.get(endpoint);

      if (response.status === 200 && response.data?.data) {
        const processData = response.data.data;
        const sortedStages = processData.sort(
          (a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0)
        );
        initializeKanbanColumns(sortedStages);
        return sortedStages;
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (error) {
      const errorMessage = `Failed to load process data: ${error.message}`;
      setError(errorMessage);
      toast.error("Failed to load process data");
      return [];
    } finally {
      setLoading(false);
    }
  }, []); // 👈 No dependencies (it’s self-contained)

  const canMoveCard = (production, targetStage, sourceStage) => {
    if (isProductionDisabled(production)) {
      return false;
    }

    const stageDifference =
      targetStage.sequenceNumber - sourceStage.sequenceNumber;

    return Math.abs(stageDifference) === 1;
  };

  const completeProduction = async (processHistoryId, messages = []) => {
    try {
      setProcessingStageChange(true);
      const authorities = JSON.parse(
        sessionStorage.getItem("Authorities") || "[]"
      );
      let endpoint = "";
      if (authorities.includes("ROLE_CRM-ADMIN")) {
        endpoint = `${process.env.REACT_APP_API_CRM_ADMIN}/process-history/${processHistoryId}/complete`;
      } else if (authorities.includes("ROLE_PRODUCTION")) {
        endpoint = `${process.env.REACT_APP_API_PROD}/process-history/${processHistoryId}/complete`;
      } else {
        throw new Error("User does not have the required role.");
      }

      const requestBody = messages.length > 0 ? messages : ["Completed"];
      const response = await axiosInstance.put(endpoint, requestBody);

      if (response.status === 200) {
        toast.success("Production moved to next stage successfully!");
        await fetchProcessData();
        return true;
      } else {
        throw new Error(
          response.data?.message || "Failed to complete production"
        );
      }
    } catch (error) {
      toast.error(`Failed to move production: ${error.message}`);
      return false;
    } finally {
      setProcessingStageChange(false);
    }
  };

  const revertProduction = async (processHistoryId, returnedMessage) => {
    try {
      setProcessingStageChange(true);
      const authorities = JSON.parse(
        sessionStorage.getItem("Authorities") || "[]"
      );
      let endpoint = "";
      if (authorities.includes("ROLE_CRM-ADMIN")) {
        endpoint = `${process.env.REACT_APP_API_CRM_ADMIN}/process-history/${processHistoryId}/revert`;
      } else if (authorities.includes("ROLE_PRODUCTION")) {
        endpoint = `${process.env.REACT_APP_API_PROD}/process-history/${processHistoryId}/revert`;
      } else {
        throw new Error("User does not have the required role.");
      }

      const response = await axiosInstance.put(endpoint, null, {
        params: { returnedMessage },
      });

      if (response.status === 200) {
        toast.success("Production moved to previous stage successfully!");
        await fetchProcessData();
        return true;
      } else {
        throw new Error(
          response.data?.message || "Failed to revert production"
        );
      }
    } catch (error) {
      toast.error(`Failed to move production: ${error.message}`);
      return false;
    } finally {
      setProcessingStageChange(false);
    }
  };
  const handleDragStart = (e, production, sourceColumnKey) => {
    if (isProductionDisabled(production)) {
      e.preventDefault();
      toast.error(
        "This production has already been processed and cannot be moved again."
      );
      return;
    }

    const dragData = {
      production,
      sourceColumnKey,
      sourceStage: kanbanColumns[sourceColumnKey].stage,
    };

    setDraggedItem(dragData);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/json", JSON.stringify(dragData));

    e.target.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedItem(null);
    setDraggedOverColumn(null);
  };

  const handleDragOver = (e, targetColumnKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDraggedOverColumn(targetColumnKey);
  };

  const handleDragLeave = () => {
    setDraggedOverColumn(null);
  };

  const handleDrop = (e, targetColumnKey) => {
    e.preventDefault();
    setDraggedOverColumn(null);

    if (!draggedItem) return;

    const { production, sourceColumnKey, sourceStage } = draggedItem;
    const targetStage = kanbanColumns[targetColumnKey].stage;

    if (sourceColumnKey === targetColumnKey) {
      return;
    }

    if (!canMoveCard(production, targetStage, sourceStage)) {
      if (isProductionDisabled(production)) {
        toast.error(
          "This production has already been processed and cannot be moved again."
        );
      } else {
        toast.error(
          "Only adjacent stage movement is allowed (move to next or previous stage only)."
        );
      }
      setDraggedItem(null);
      return;
    }

    const stageDifference =
      targetStage.sequenceNumber - sourceStage.sequenceNumber;

    if (stageDifference === 1) {
      setSelectedProduction(production);
      setSelectedTargetStage(targetStage);
      setShowCompleteModal(true);
    } else if (stageDifference === -1) {
      setSelectedProduction(production);
      setSelectedTargetStage(targetStage);
      setShowRevertModal(true);
    }

    setDraggedItem(null);
  };

  const handleCompleteSubmit = async () => {
    if (!selectedProduction) return;

    const validMessages = completeMessages.filter((msg) => msg.trim());

    const success = await completeProduction(
      selectedProduction.processHistoryId,
      validMessages
    );

    if (success) {
      setShowCompleteModal(false);
      setSelectedProduction(null);
      setSelectedTargetStage(null);
      setCompleteMessages([""]);
    }
  };

  const handleRevertSubmit = async () => {
    if (!selectedProduction || !revertMessage.trim()) {
      toast.error("Please provide a reason for reverting this production");
      return;
    }

    const success = await revertProduction(
      selectedProduction.processHistoryId,
      revertMessage.trim()
    );

    if (success) {
      setShowRevertModal(false);
      setSelectedProduction(null);
      setSelectedTargetStage(null);
      setRevertMessage("");
    }
  };

  const addMessageInput = () => {
    setCompleteMessages([...completeMessages, ""]);
  };

  const removeMessageInput = (index) => {
    if (completeMessages.length > 1) {
      const updated = completeMessages.filter((_, i) => i !== index);
      setCompleteMessages(updated);
    }
  };

  const updateMessageInput = (index, value) => {
    const updated = [...completeMessages];
    updated[index] = value;
    setCompleteMessages(updated);
  };

  const initializeKanbanColumns = (processData = []) => {
    const columns = {};

    processData.forEach((stageData) => {
      const columnKey = `stage-${stageData.processStageId}`;

      columns[columnKey] = {
        stage: {
          processStageId: stageData.processStageId,
          name: stageData.name,
          description: stageData.description,
          sequenceNumber: stageData.sequenceNumber,
          isUserStage: stageData.isUserStage,
          employeeIds: stageData.employeeIds,
        },
        productions: stageData.processHistories || [],
      };
    });

    setKanbanColumns(columns);
  };

  const getFilteredProductions = (productions) => {
    if (!searchQuery.trim()) {
      return productions;
    }

    const lowerQuery = searchQuery.toLowerCase();
    return productions.filter((production) =>
      production.productName?.toLowerCase().includes(lowerQuery)
    );
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  useEffect(() => {
    const initializeData = async () => {
      await fetchEmployees();
      await fetchProcessData();
    };

    initializeData();
  }, [fetchProcessData]);

  return (
    <div className="productions-container">
      <div className="productions-header">
        <div className="header-info">
          <h1>Productions Management</h1>
        </div>

        <div className="header-actions">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search productions..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button className="clear-search-btn" onClick={clearSearch}>
                <Icon icon="material-symbols:close" />
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="production-loading-state">
          <div className="production-loading-content">
            <Icon
              icon="material-symbols:hourglass-bottom"
              className="loading-icon spinning"
            />
            <h3>Loading Process Data</h3>
          </div>
        </div>
      ) : error ? (
        <div className="error-state">
          <div className="error-content">
            <Icon
              icon="material-symbols:error-outline"
              className="error-icon"
            />
            <h3>Something went wrong</h3>
            <p>{error}</p>
          </div>
        </div>
      ) : Object.keys(kanbanColumns).length === 0 ? (
        <div className="empty-state">
          <div className="empty-content">
            <Icon
              icon="material-symbols:inventory-2-outline"
              className="empty-icon"
            />
            <h3>No Process Stages Found</h3>
            <p>
              Please configure process stages to start managing productions.
            </p>
          </div>
        </div>
      ) : (
        <div className="kanban-board">
          {Object.keys(kanbanColumns)
            .sort((a, b) => {
              const stageA = kanbanColumns[a].stage;
              const stageB = kanbanColumns[b].stage;
              return (
                (stageA.sequenceNumber || 0) - (stageB.sequenceNumber || 0)
              );
            })
            .map((columnKey) => {
              const column = kanbanColumns[columnKey];
              const stage = column.stage;
              const isDraggedOver = draggedOverColumn === columnKey;
              const filteredProductions = getFilteredProductions(
                column.productions
              );
              const assignedEmployees = getAssignedEmployees(stage);

              return (
                <div
                  className={`kanban-column ${
                    isDraggedOver ? "drag-over" : ""
                  }`}
                  key={columnKey}
                  onDragOver={(e) => handleDragOver(e, columnKey)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, columnKey)}
                >
                  <div className="column-header">
                    <div className="column-title-wrapper">
                      <h2 className="column-title">{stage.name}</h2>
                      <span className="card-count">
                        {filteredProductions.length}
                      </span>
                    </div>

                    {stage.description && (
                      <p className="column-description">{stage.description}</p>
                    )}

                    <div className="stage-indicators">
                      {stage.isUserStage && (
                        <span className="stage-badge user">
                          <Icon icon="material-symbols:person" />
                          User Stage
                        </span>
                      )}
                    </div>

                    {assignedEmployees.length > 0 && (
                      <div className="assigned-employees">
                        <h4 className="employees-title">
                          <Icon icon="material-symbols:group" />
                          Assigned Employees
                        </h4>
                        <div className="employees-list">
                          {assignedEmployees.map((employee) => (
                            <div key={employee.empId} className="employee-item">
                              <div className="employee-info">
                                <span className="employee-name">
                                  {employee.firstName} {employee.lastName}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="column-content">
                    {filteredProductions.length === 0 ? (
                      <div className="empty-column">
                        <Icon
                          icon={
                            searchQuery
                              ? "material-symbols:search-off"
                              : "material-symbols:inbox-outline"
                          }
                          className="empty-icon"
                        />
                        <h4>
                          {searchQuery
                            ? "No matches found"
                            : "No productions yet"}
                        </h4>
                        <p>
                          {searchQuery
                            ? "Try adjusting your search terms"
                            : "Productions will appear here when added to this stage"}
                        </p>
                      </div>
                    ) : (
                      <div className="kanban-cards">
                        {filteredProductions.map((production) => {
                          const isDisabled = isProductionDisabled(production);

                          return (
                            <div
                              className={`kanban-card ${
                                isDisabled ? "disabled" : ""
                              }`}
                              key={production.processHistoryId}
                              draggable={!isDisabled}
                              onDragStart={(e) => {
                                if (isDisabled) {
                                  e.preventDefault();
                                  toast.error(
                                    "This production has already been processed and cannot be moved again."
                                  );
                                  return false;
                                }
                                handleDragStart(e, production, columnKey);
                              }}
                              onDragEnd={handleDragEnd}
                              style={{
                                cursor: isDisabled ? "not-allowed" : "grab",
                                opacity: isDisabled ? 0.6 : 1,
                              }}
                            >
                              <div className="card-drag-handle">
                                <Icon
                                  icon={
                                    isDisabled
                                      ? "material-symbols:lock"
                                      : "material-symbols:drag-indicator"
                                  }
                                />
                              </div>

                              <div className="card-header">
                                <div className="card-header-left">
                                  <Icon
                                    icon="material-symbols:inventory"
                                    className="product-icon"
                                  />
                                  <h4 className="product-name">
                                    {production.productName}
                                  </h4>
                                </div>
                                <div className="card-badges">
                                  {production.returned && (
                                    <span className="returned-badge">
                                      <Icon icon="material-symbols:keyboard-return" />
                                      Returned
                                    </span>
                                  )}
                                  {production.status === "COMPLETED" && (
                                    <span className="completed-badge">
                                      <Icon icon="material-symbols:check-circle" />
                                      Completed
                                    </span>
                                  )}
                                  {isDisabled && (
                                    <span className="disabled-badge">
                                      <Icon icon="material-symbols:lock" />
                                      Processed
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="card-content">
                                <div className="card-detail">
                                  <Icon
                                    icon="material-symbols:inventory-2"
                                    className="detail-icon"
                                  />
                                  <span>
                                    Qty: {production.quantity || 0} units
                                  </span>
                                </div>

                                {production.employeeAssignedName && (
                                  <div className="card-detail">
                                    <Icon
                                      icon="material-symbols:person"
                                      className="detail-icon"
                                    />
                                    <span>
                                      Assigned:{" "}
                                      {production.employeeAssignedName}
                                    </span>
                                  </div>
                                )}

                                {production.status && (
                                  <div className="card-detail">
                                    <Icon
                                      icon="material-symbols:info"
                                      className="detail-icon"
                                    />
                                    <span>Status: {production.status}</span>
                                  </div>
                                )}

                                {production.lastMovedAt && (
                                  <div className="card-detail">
                                    <Icon
                                      icon="material-symbols:schedule"
                                      className="detail-icon"
                                    />
                                    <span>
                                      Moved:{" "}
                                      {new Date(
                                        production.lastMovedAt
                                      ).toLocaleString()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {showCompleteModal && (
        <div className="modal-overlay">
          <div className="create-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Complete Production Stage</h2>
              <button
                className="close-btn"
                onClick={() => setShowCompleteModal(false)}
                disabled={processingStageChange}
              >
                <Icon icon="material-symbols:close" />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                <div className="production-info">
                  <h3>{selectedProduction?.productName}</h3>
                  <p>
                    Moving to: <strong>{selectedTargetStage?.name}</strong>
                  </p>
                </div>

                <div className="form-section">
                  <h4>Completion Messages (Optional)</h4>
                  <p>Add any notes or messages for completing this stage:</p>

                  {completeMessages.map((message, index) => (
                    <div key={index} className="message-input-group">
                      <div className="form-row">
                        <div className="enquiriesform-group">
                          <label>Message {index + 1}</label>
                          <textarea
                            value={message}
                            onChange={(e) =>
                              updateMessageInput(index, e.target.value)
                            }
                            placeholder="Enter completion message (optional)"
                            rows={3}
                            disabled={processingStageChange}
                          />
                        </div>
                        {completeMessages.length > 1 && (
                          <button
                            type="button"
                            className="remove-message-btn"
                            onClick={() => removeMessageInput(index)}
                            disabled={processingStageChange}
                          >
                            <Icon icon="material-symbols:delete" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="add-message-btn"
                    onClick={addMessageInput}
                    disabled={processingStageChange}
                  >
                    <Icon icon="material-symbols:add" />
                    Add Another Message
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowCompleteModal(false)}
                disabled={processingStageChange}
              >
                Cancel
              </button>

              <button
                className="btn-primary"
                onClick={handleCompleteSubmit}
                disabled={processingStageChange}
              >
                {processingStageChange ? (
                  <>
                    <Icon
                      icon="material-symbols:hourglass-bottom"
                      className="spinning"
                    />
                    Processing...
                  </>
                ) : (
                  <>
                    <Icon icon="material-symbols:check-circle" />
                    Complete Stage
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRevertModal && (
        <div className="modal-overlay">
          <div className="create-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Revert Production Stage</h2>
              <button
                className="close-btn"
                onClick={() => setShowRevertModal(false)}
                disabled={processingStageChange}
              >
                <Icon icon="material-symbols:close" />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                <div className="production-info">
                  <h3>{selectedProduction?.productName}</h3>
                  <p>
                    Moving to: <strong>{selectedTargetStage?.name}</strong>
                  </p>
                </div>

                <div className="form-section">
                  <h4>Reason for Reverting *</h4>
                  <p>Please provide a reason for reverting this production:</p>

                  <div className="enquiriesform-group">
                    <label>Revert Message</label>
                    <textarea
                      value={revertMessage}
                      onChange={(e) => setRevertMessage(e.target.value)}
                      placeholder="Enter reason for reverting this production"
                      rows={4}
                      disabled={processingStageChange}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowRevertModal(false)}
                disabled={processingStageChange}
              >
                Cancel
              </button>

              <button
                className="btn-primary"
                onClick={handleRevertSubmit}
                disabled={processingStageChange}
              >
                {processingStageChange ? (
                  <>
                    <Icon
                      icon="material-symbols:hourglass-bottom"
                      className="spinning"
                    />
                    Processing...
                  </>
                ) : (
                  <>
                    <Icon icon="material-symbols:undo" />
                    Revert Stage
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Productions;
